import Store from 'repatch';

import State from '../core/State';
import { BackgroundChargeJobStatus } from '../entity/BackgroundChargeJob';
import { AccountType } from '../entity/BankAccount';
import BillUser, { BillMethod } from '../entity/BillUser';
import PaymentExtProvider, { ProviderType } from '../entity/PaymentExtProvider';
import ProductPricing from '../entity/ProductPricing';
import {
  setBankAccount,
  setBankAccountAgent,
  setBillUser,
  setPaymentDetails,
  setPaymentDetailsAgent,
  setPaymentExtProvider,
  setPaymentExtProviderAgent,
  setPaymentHistory,
  setPaymentHistoryAgent,
  setProductPricing,
  setUserPayee,
  setUserPayeeAgent,
  setZenginBank,
  setZenginBankAgent,
} from '../reducer/acct';
import { finishSpinner, startSpinner } from '../reducer/fullScreenSpinner';
import ApiErrorHandler from '../service/ApiErrorHandler';
import MessageDispatcher from '../service/MessageDispatcher';
import AcctWebApi from '../service/persistent/AcctWebApi';
import sleep from '../utility/sleep';
import { setCurrentAcctAccount, setCurrentAcctAccountLoading } from './../reducer/organization';
import BfUsecase from './BfUsecase';

class JobError extends Error {}

class AcctUsecase {
  private store: Store<State>;
  private apiErrorHandler: ApiErrorHandler;
  private acctWebApi: AcctWebApi;
  private messageDispatcher: MessageDispatcher;
  private bfUsecase: BfUsecase;

  constructor({
    store,
    apiErrorHandler,
    acctWebApi,
    messageDispatcher,
    bfUsecase,
  }: {
    store: Store<State>;
    apiErrorHandler: ApiErrorHandler;
    acctWebApi: AcctWebApi;
    messageDispatcher: MessageDispatcher;
    bfUsecase: BfUsecase;
  }) {
    this.store = store;
    this.apiErrorHandler = apiErrorHandler;
    this.acctWebApi = acctWebApi;
    this.messageDispatcher = messageDispatcher;
    this.bfUsecase = bfUsecase;
  }

  public getBillUser = async (kaizenUserId: string): Promise<void> => {
    try {
      const billUser = await this.acctWebApi.getBillUser(kaizenUserId);
      this.store.dispatch(setBillUser(billUser));
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 404) {
        this.store.dispatch(
          setBillUser(
            new BillUser({
              kaizenUserId,
              countryOfResidence: 'JP',
            }),
          ),
        );
      } else {
        throw err;
      }
    }
  };

  public getAccount = async (organizationId: string): Promise<void> => {
    this.store.dispatch(setCurrentAcctAccountLoading(true));
    const account = await this.acctWebApi.getAccount(organizationId);
    this.store.dispatch(setCurrentAcctAccount(account));
    this.store.dispatch(setCurrentAcctAccountLoading(false));
  };

  public createCard = async ({
    organizationId,
    email,
    token,
  }: {
    organizationId: string;
    email: string;
    token: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const formData = new FormData();
      formData.append('kaizen_user_id', organizationId);
      formData.append('email', email);
      formData.append('token', token);
      formData.append('legal_entity', '1');

      await this.acctWebApi.createCard(formData);
      await this.getBillUser(organizationId);
      this.messageDispatcher.notifyUnimportant('account_setting.payments.card_saved');
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 402) {
        this.messageDispatcher.notifyUnimportant(err.response.data.errors[0].message, {}, { error: true });
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public getProductPricing = async (kaizenUserId: string): Promise<void> => {
    try {
      const productPricing = await this.acctWebApi.getProductPricing(kaizenUserId);
      this.store.dispatch(setProductPricing(productPricing));
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 404) {
        // business_region = 'global' の user は 404 がくる
        this.store.dispatch(
          setProductPricing(
            new ProductPricing({
              flatRate: 0,
              taxRate: 0,
              expirationDays: 0,
              currency: 'USD',
            }),
          ),
        );
      } else {
        throw err;
      }
    }
  };

  public chargePoint = async ({
    organizationId,
    quantity,
    bill_method,
    billing_to_id,
  }: {
    organizationId: string;
    quantity: number;
    bill_method: BillMethod;
    billing_to_id: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const formData = new FormData();
      formData.append('kaizen_user_id', organizationId);
      formData.append('quantity', String(quantity));
      formData.append('bill_method', bill_method);
      formData.append('billing_to_id', billing_to_id);
      const jobId = await this.acctWebApi.chargePoint(formData);
      await this.waitJobDone(jobId);
      await this.bfUsecase.getBudget(organizationId);
      this.messageDispatcher.notifyUnimportant('account_setting.points.point_charged');
    } catch (err) {
      if (err instanceof JobError) {
        this.messageDispatcher.notifyUnimportant(err.message, {}, { error: true });
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  private waitJobDone = async (jobId: string): Promise<void> => {
    let i = 0;
    const getJobStatusWhileRunning = async () => {
      const job = await this.acctWebApi.getBackgroundChargeJob(jobId);
      if (job.status === BackgroundChargeJobStatus.RUNNING) {
        if (i < 20) {
          i++;
          await sleep(1000);
          await getJobStatusWhileRunning();
        } else {
          throw new JobError('TimeoutError');
        }
      } else if (job.status === BackgroundChargeJobStatus.FAILED) {
        throw new JobError(job.errors);
      } else if (job.status === BackgroundChargeJobStatus.SUCCEEDED) {
        return;
      } else {
        throw new JobError('UnknownError');
      }
    };
    await getJobStatusWhileRunning();
  };

  public getZenginBank = async (bankCode: string, branchCode?: string, isAgent?: boolean): Promise<void> => {
    try {
      const zenginBank = await this.acctWebApi.getZenginBank(bankCode, branchCode);
      if (branchCode && !zenginBank.branch) {
        this.messageDispatcher.notifyUnimportant('user_setting.payment.bank.branch_code_not_found');
      }
      if (isAgent) {
        this.store.dispatch(setZenginBankAgent(zenginBank));
      } else {
        this.store.dispatch(setZenginBank(zenginBank));
      }
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 404) {
        this.messageDispatcher.notifyUnimportant('user_setting.payment.bank.bank_code_not_found');
        if (isAgent) {
          this.store.dispatch(setZenginBankAgent(undefined));
        } else {
          this.store.dispatch(setZenginBank(undefined));
        }
      } else {
        throw err;
      }
    }
  };

  public getUserPayee = async (kaizenUserId: string, agentId?: string): Promise<void> => {
    kaizenUserId;
    const userPayee = await this.acctWebApi.getUserPayee(agentId ?? '');
    if (agentId) {
      this.store.dispatch(setUserPayeeAgent(userPayee));
    } else {
      this.store.dispatch(setUserPayee(userPayee));
    }
  };

  public createUserPayee = async ({
    kaizenUserId,
    agentId,
    lastName,
    firstName,
    agentName,
    agentRepresentativeName,
    countryOfResidence,
    nationality,
    optional,
    streetAddress,
    cityName,
    province,
    postalCode,
    phoneNumber,
  }: {
    kaizenUserId: string;
    agentId?: string;
    lastName: string;
    firstName: string;
    agentName: string;
    agentRepresentativeName: string;
    countryOfResidence: string;
    nationality: string;
    optional: string;
    streetAddress: string;
    cityName: string;
    province: string;
    postalCode: string;
    phoneNumber: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const formData = new FormData();
      formData.append('payment_user_payee[country_of_residence]', countryOfResidence);
      formData.append('payment_user_payee[optional]', optional);
      formData.append('payment_user_payee[street_address]', streetAddress);
      formData.append('payment_user_payee[city_name]', cityName);
      formData.append('payment_user_payee[province]', province);
      formData.append('payment_user_payee[postal_code]', postalCode);
      formData.append('payment_user_payee[phone_number]', phoneNumber);
      if (agentId) {
        formData.append('agent_id', agentId);
        formData.append('payment_user_payee[agent_name]', agentName);
        formData.append('payment_user_payee[agent_representative_name]', agentRepresentativeName);
      } else {
        formData.append('kaizen_user_id', kaizenUserId);
        formData.append('payment_user_payee[last_name]', lastName);
        formData.append('payment_user_payee[first_name]', firstName);
        formData.append('payment_user_payee[nationality]', nationality);
      }

      await this.acctWebApi.createUserPayee(formData);
      const state = this.store.getState();
      await this.getUserPayee(state.authentication.signedInUser!.id, agentId);
      this.messageDispatcher.notifyUnimportant('user_setting.payment.payee.settings_registered');
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 400) {
        this.messageDispatcher.notifyUnimportant(
          err.response.data.errors.map((e: any) => e.message).join('\n'),
          {},
          { error: true },
        );
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public updateUserPayee = async ({
    id,
    agentId,
    lastName,
    firstName,
    agentName,
    agentRepresentativeName,
    countryOfResidence,
    nationality,
    optional,
    streetAddress,
    cityName,
    province,
    postalCode,
    phoneNumber,
  }: {
    id: string;
    agentId?: string;
    lastName: string;
    firstName: string;
    agentName: string;
    agentRepresentativeName: string;
    countryOfResidence: string;
    nationality: string;
    optional: string;
    streetAddress: string;
    cityName: string;
    province: string;
    postalCode: string;
    phoneNumber: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const formData = new FormData();
      formData.append('payment_user_payee[country_of_residence]', countryOfResidence);
      formData.append('payment_user_payee[optional]', optional);
      formData.append('payment_user_payee[street_address]', streetAddress);
      formData.append('payment_user_payee[city_name]', cityName);
      formData.append('payment_user_payee[province]', province);
      formData.append('payment_user_payee[postal_code]', postalCode);
      formData.append('payment_user_payee[phone_number]', phoneNumber);
      if (agentId) {
        formData.append('payment_user_payee[agent_name]', agentName);
        formData.append('payment_user_payee[agent_representative_name]', agentRepresentativeName);
      } else {
        formData.append('payment_user_payee[last_name]', lastName);
        formData.append('payment_user_payee[first_name]', firstName);
        formData.append('payment_user_payee[nationality]', nationality);
      }

      await this.acctWebApi.updateUserPayee(id, formData);
      const state = this.store.getState();
      await this.getUserPayee(state.authentication.signedInUser!.id, agentId);
      this.messageDispatcher.notifyUnimportant('user_setting.payment.payee.settings_updated');
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 400) {
        this.messageDispatcher.notifyUnimportant(
          err.response.data.errors.map((e: any) => e.message).join('\n'),
          {},
          { error: true },
        );
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public getBankAccount = async (kaizenUserId: string, agentId?: string): Promise<void> => {
    kaizenUserId;
    const bankAccount = await this.acctWebApi.getBankAccount(agentId ?? '');
    if (agentId) {
      this.store.dispatch(setBankAccountAgent(bankAccount));
    } else {
      this.store.dispatch(setBankAccount(bankAccount));
    }
  };

  public createBankAccount = async ({
    kaizenUserId,
    agentId,
    bankCode,
    branchCode,
    accountType,
    accountNumber,
    accountName,
  }: {
    kaizenUserId: string;
    agentId?: string;
    bankCode: string;
    branchCode: string;
    accountType: AccountType;
    accountNumber: string;
    accountName: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const formData = new FormData();
      formData.append('payment_bank_account[bank_code]', bankCode);
      formData.append('payment_bank_account[branch_code]', branchCode);
      formData.append('payment_bank_account[account_type]', accountType.toLowerCase());
      formData.append('payment_bank_account[account_number]', accountNumber);
      formData.append('payment_bank_account[account_name]', accountName);
      if (agentId) {
        formData.append('agent_id', agentId);
      } else {
        formData.append('kaizen_user_id', kaizenUserId);
      }

      await this.acctWebApi.createBankAccount(formData);
      const state = this.store.getState();
      await this.getBankAccount(state.authentication.signedInUser!.id, agentId);
      await this.getPaymentExtProvider(state.authentication.signedInUser!.id, agentId);
      this.messageDispatcher.notifyUnimportant('user_setting.payment.bank.settings_registered');
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 400) {
        this.messageDispatcher.notifyUnimportant(
          err.response.data.errors.map((e: any) => e.message).join('\n'),
          {},
          { error: true },
        );
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public updateBankAccount = async ({
    id,
    agentId,
    bankCode,
    branchCode,
    accountType,
    accountNumber,
    accountName,
  }: {
    id: string;
    agentId?: string;
    bankCode: string;
    branchCode: string;
    accountType: AccountType;
    accountNumber: string;
    accountName: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      const formData = new FormData();
      formData.append('payment_bank_account[bank_code]', bankCode);
      formData.append('payment_bank_account[branch_code]', branchCode);
      formData.append('payment_bank_account[account_type]', accountType.toLowerCase());
      formData.append('payment_bank_account[account_number]', accountNumber);
      formData.append('payment_bank_account[account_name]', accountName);

      if (agentId) {
        formData.append('agent_id', agentId);
      }

      await this.acctWebApi.updateBankAccount(id, formData);
      const state = this.store.getState();
      await this.getBankAccount(state.authentication.signedInUser!.id, agentId);
      await this.getPaymentExtProvider(state.authentication.signedInUser!.id, agentId);
      this.messageDispatcher.notifyUnimportant('user_setting.payment.bank.settings_updated');
    } catch (_err) {
      const err: any = _err;
      if (err.response && err.response.status === 400) {
        this.messageDispatcher.notifyUnimportant(
          err.response.data.errors.map((e: any) => e.message).join('\n'),
          {},
          { error: true },
        );
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public getPaymentHistory = async (kaizenUserId: string, agentId?: string): Promise<void> => {
    kaizenUserId;
    const paymentHistory = await this.acctWebApi.getPaymentHistory(agentId ?? '');
    if (agentId) {
      this.store.dispatch(setPaymentHistoryAgent(paymentHistory));
    } else {
      this.store.dispatch(setPaymentHistory(paymentHistory));
    }
  };

  public getPaymentDetails = async (kaizenUserId: string, serviceMonth: Date, agentId?: string): Promise<void> => {
    const paymentDetails = await this.acctWebApi.getPaymentDetails(kaizenUserId, serviceMonth, agentId);
    if (agentId) {
      this.store.dispatch(setPaymentDetailsAgent(paymentDetails));
    } else {
      this.store.dispatch(setPaymentDetails(paymentDetails));
    }
  };

  public getPaymentExtProvider = async (kaizenUserId: string, agentId?: string): Promise<void> => {
    const paymentExtProvider = await this.acctWebApi.getPaymentExtProvider(kaizenUserId, agentId ?? '');
    if (agentId) {
      if (paymentExtProvider) {
        this.store.dispatch(setPaymentExtProviderAgent(paymentExtProvider));
      } else {
        this.store.dispatch(setPaymentExtProviderAgent(new PaymentExtProvider({ providerType: ProviderType.NONE })));
      }
    } else {
      if (paymentExtProvider) {
        this.store.dispatch(setPaymentExtProvider(paymentExtProvider));
      } else {
        this.store.dispatch(setPaymentExtProvider(new PaymentExtProvider({ providerType: ProviderType.NONE })));
      }
    }
  };
}

export default AcctUsecase;
